import React, { memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  RoomContext,
  type TrackReference,
  type TrackReferenceOrPlaceholder
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import { useVideoCall } from '../../hooks/useVideoCall.js';
import { useVideoOverlayVisibility } from '../../hooks/useVideoOverlayVisibility.js';
import { useOpenDrawers } from '../../hooks/useOpenDrawers.js';
import { useCallTiles } from '../../hooks/useCallTiles.js';
import { useVideoView } from '../../hooks/useVideoView.js';
import { VideoViewContext } from '../../contexts/videoViewContext.js';
import { isCallLive } from '../../contexts/videoViewReducer.js';
import { VideoFrame } from './VideoFrame.js';
import { VideoTileGrid } from './VideoTileGrid.js';
import { VideoRoomAudio } from './VideoRoomAudio.js';
import { FullScreenVideo } from './FullScreenVideo.js';
import { FullScreenPill } from './FullScreenPill.js';
import { TiledLayout } from './TiledLayout.js';
import { PinnedLayout } from './PinnedLayout.js';
import { ShareChip } from './ShareChip.js';
import { displayNameOf, tileKey } from './track-references.js';
import { VIDEO_CONFIG, getStackPositionClass } from '../../config/videoConfig.js';

/** The call's tiles in the stable order, and the microphones for their speaking rings */
interface CallTilesProps {
  tiles: TrackReferenceOrPlaceholder[];
  microphoneTrackRefs: ReadonlyMap<string, TrackReference>;
}

/**
 * The heads-up tiles: one row docked at the top of the screen (#131), one
 * tile per published track, in a stable order (the local participant, then
 * everyone else by join time), from {@link useCallTiles}. Speaking and camera
 * state deliberately play no part, or the row would reshuffle whenever
 * someone talks (#104).
 *
 * `actions` is the line under the tiles, beside the `+N` chip: the "Full
 * screen" pill when this viewer can go full screen.
 *
 * @internal
 */
const VideoOverlays: React.FC<CallTilesProps & { actions?: React.ReactNode }> = ({
  tiles,
  microphoneTrackRefs,
  actions
}) => {
  const openDrawers = useOpenDrawers();

  if (tiles.length === 0) {
    return (
      <div
        className={`${getStackPositionClass(openDrawers)} ${VIDEO_CONFIG.styles.zIndex} pointer-events-none`}
        data-lk-theme={VIDEO_CONFIG.theme}
      >
        <VideoFrame isLocal isEmpty>
          <div className="flex h-full w-full items-center justify-center">
            <p className={`${VIDEO_CONFIG.styles.noVideoText} text-xs`}>
              {VIDEO_CONFIG.messages.waiting}
            </p>
          </div>
        </VideoFrame>
      </div>
    );
  }

  return (
    <VideoTileGrid trackRefs={tiles} microphoneTrackRefs={microphoneTrackRefs} actions={actions} />
  );
};

/**
 * The call full screen (#123, #124): the overlay with the tiled layout, or
 * the pinned layout while the pinned tile is in the call.
 *
 * The pin lives in the view state (`useVideoView`). The top bar offers the
 * most recent screen share that is not pinned while something else is, as
 * "<name> is sharing · Pin" (epic D10).
 *
 * Pinning, unpinning and swapping change the layout, which remounts the
 * tiles' pin buttons. Keyboard focus follows the tile whose button was used,
 * and comes back to the same tile's button when a layout change the viewer
 * did not ask for (a share pinning itself, the pinned person leaving) drops
 * it.
 *
 * @internal
 */
const FullScreenCall: React.FC<CallTilesProps & {
  label?: string;
  chatDrawerWidth: number;
  status: React.ReactNode;
  onExit: () => void;
}> = ({ tiles, microphoneTrackRefs, label, chatDrawerWidth, status, onExit }) => {
  const { pinnedKey, pin, unpin, offeredShareKey } = useVideoView();
  const participantCount = new Set(tiles.map((tile) => tile.participant.identity)).size;

  // The pinned layout only while the pinned tile is here: until the view
  // state catches up with a tile that has gone, the tiled layout shows.
  const pinned = useMemo(
    () => (pinnedKey === null ? undefined : tiles.find((tile) => tileKey(tile) === pinnedKey)),
    [tiles, pinnedKey]
  );
  const others = useMemo(
    () => (pinned ? tiles.filter((tile) => tile !== pinned) : tiles),
    [tiles, pinned]
  );
  const offeredShare = pinned && offeredShareKey !== null
    ? tiles.find((tile) => tileKey(tile) === offeredShareKey)
    : undefined;

  const focusRequestRef = useRef<string | null>(null);
  const lastFocusedKeyRef = useRef<string | null>(null);

  const handlePin = useCallback((key: string) => {
    focusRequestRef.current = key;
    pin(key);
  }, [pin]);

  const handleUnpin = useCallback((key: string) => {
    focusRequestRef.current = key;
    unpin();
  }, [unpin]);

  const offeredKey = offeredShare ? tileKey(offeredShare) : null;
  const pinOfferedShare = useCallback(() => {
    if (offeredKey !== null) handlePin(offeredKey);
  }, [offeredKey, handlePin]);

  // A layout change nobody asked for unmounted the focused pin button:
  // hand focus to the same tile's button in the new layout when it mounts.
  const layoutKey = pinned ? `pinned:${tileKey(pinned)}` : 'tiled';
  useLayoutEffect(() => {
    if (focusRequestRef.current !== null) return;
    const focusLost = !document.activeElement || document.activeElement === document.body;
    if (focusLost) focusRequestRef.current = lastFocusedKeyRef.current;
  }, [layoutKey]);

  // Never hand focus to a tile that has gone; it might come back much later.
  useEffect(() => {
    const requested = focusRequestRef.current;
    if (requested !== null && !tiles.some((tile) => tileKey(tile) === requested)) {
      focusRequestRef.current = null;
    }
  }, [tiles]);

  const trackFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    lastFocusedKeyRef.current = event.target instanceof HTMLElement
      ? event.target.dataset.pinKey ?? null
      : null;
  }, []);

  const forgetFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && !event.currentTarget.contains(next)) {
      lastFocusedKeyRef.current = null;
    }
  }, []);

  return (
    <FullScreenVideo
      label={label}
      participantCount={participantCount}
      chatDrawerWidth={chatDrawerWidth}
      topBarSlot={offeredShare && (
        <ShareChip name={displayNameOf(offeredShare)} onPin={pinOfferedShare} />
      )}
      status={status}
      onExit={onExit}
    >
      <div className="h-full w-full" onFocus={trackFocus} onBlur={forgetFocus}>
        {pinned ? (
          <PinnedLayout
            pinned={pinned}
            others={others}
            microphoneTrackRefs={microphoneTrackRefs}
            onPin={handlePin}
            onUnpin={handleUnpin}
            focusRequestRef={focusRequestRef}
          />
        ) : (
          <TiledLayout
            tiles={tiles}
            microphoneTrackRefs={microphoneTrackRefs}
            onPin={handlePin}
            focusRequestRef={focusRequestRef}
          />
        )}
      </div>
    </FullScreenVideo>
  );
};

/**
 * Everything that shows the call inside `RoomContext`. It reads the tiles
 * once for both views and reports them to the view state, whichever view is
 * showing, so the pin follows the call: a pin whose tile has gone clears, a
 * share that ended is forgotten, and a new share can pin itself (#124). Then
 * it renders the corner tiles or full screen.
 *
 * @internal
 */
const CallView: React.FC<{
  showFullScreen: boolean;
  showCorner: boolean;
  actions?: React.ReactNode;
  label?: string;
  chatDrawerWidth: number;
  status: React.ReactNode;
  onExit: () => void;
}> = ({ showFullScreen, showCorner, actions, label, chatDrawerWidth, status, onExit }) => {
  const { tiles, microphoneTrackRefs } = useCallTiles();
  const syncTiles = useContext(VideoViewContext)?.syncTiles;

  const { keys, shareKeys } = useMemo(() => ({
    keys: tiles.map(tileKey),
    shareKeys: tiles.filter((tile) => tile.source === Track.Source.ScreenShare).map(tileKey)
  }), [tiles]);

  // Before paint, so a share that pins itself never flashes the tiled layout
  // first. No tiles means the room has not reported anyone yet (the local
  // participant always has one), not that everyone left.
  useLayoutEffect(() => {
    if (!syncTiles || keys.length === 0) return;
    syncTiles(keys, shareKeys);
  }, [syncTiles, keys, shareKeys]);

  if (showFullScreen) {
    return (
      <FullScreenCall
        tiles={tiles}
        microphoneTrackRefs={microphoneTrackRefs}
        label={label}
        chatDrawerWidth={chatDrawerWidth}
        status={status}
        onExit={onExit}
      />
    );
  }

  return showCorner
    ? <VideoOverlays tiles={tiles} microphoneTrackRefs={microphoneTrackRefs} actions={actions} />
    : null;
};

/**
 * Where a status panel sits: in the corner slot over the world, or laid over
 * the top of the video area inside full screen.
 */
type StatusPlacement = 'hud' | 'fullscreen';

/**
 * Shared chrome for every status panel, so they all sit in the same slot: the
 * top of the screen, where the tiles will appear (#131), or the top of the
 * video area inside full screen.
 */
const VideoStatusPanel: React.FC<{
  borderClass: string;
  role?: 'status' | 'alert';
  testId: string;
  placement?: StatusPlacement;
  children: React.ReactNode;
}> = ({ borderClass, role = 'status', testId, placement = 'hud', children }) => {
  const openDrawers = useOpenDrawers();

  return (
    <div
      // Over the world, the strip spans the free width, so it lets clicks
      // through to the map; inside full screen the overlay places it. Either
      // way the panel carries buttons, so it opts into pointer events
      // explicitly rather than relying on what `GameUI`'s root does or does
      // not inherit.
      className={
        placement === 'hud'
          ? `${getStackPositionClass(openDrawers)} ${VIDEO_CONFIG.styles.zIndex} pointer-events-none`
          : undefined
      }
      data-lk-theme={VIDEO_CONFIG.theme}
    >
      <div
        role={role}
        aria-live={role === 'alert' ? 'assertive' : 'polite'}
        data-testid={testId}
        className={`pointer-events-auto bg-game-dark/95 rounded-lg shadow-2xl border ${borderClass} max-w-sm px-4 py-3`}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * Connection error panel.
 *
 * The copy is the *classified* message from {@link videoErrorHandler}, not
 * `error.message`: "WebSocket closed with code 1006" is not something to put
 * in front of a user. The raw message stays available as a fallback for
 * errors the classifier has never seen.
 *
 * @internal
 */
const VideoErrorPanel: React.FC<{
  message: string;
  recoveryActions: readonly { label: string; description: string }[];
  connectionAttempts: number;
  maxReconnectAttempts: number;
  isRetrying: boolean;
  onRetry: () => void;
  placement?: StatusPlacement;
}> = ({ message, recoveryActions, connectionAttempts, maxReconnectAttempts, isRetrying, onRetry, placement }) => (
  <VideoStatusPanel borderClass="border-red-500/30" role="alert" testId="video-error-panel" placement={placement}>
    <div className="flex items-start">
      <span className="text-red-500 text-xl mr-3" aria-hidden="true">⚠️</span>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-red-400 mb-1">
          Video Connection Failed
        </h3>
        <p className="text-xs text-game-light/80 mb-2" data-testid="video-error-message">
          {message}
        </p>
        {recoveryActions.length > 0 && (
          <ul className="text-xs text-game-light/60 mb-2 list-disc list-inside space-y-0.5">
            {recoveryActions.slice(0, 2).map((action) => (
              <li key={action.label} title={action.description}>
                {action.description}
              </li>
            ))}
          </ul>
        )}
        {isRetrying && connectionAttempts < maxReconnectAttempts ? (
          <p className="text-xs text-yellow-400">
            Retrying... (Attempt {connectionAttempts}/{maxReconnectAttempts})
          </p>
        ) : (
          <button
            onClick={onRetry}
            className="text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-3 py-1 rounded transition-all"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  </VideoStatusPanel>
);

/**
 * Connecting panel.
 *
 * The token round-trip allows 10s and the LiveKit connect another 15s, so
 * without this there are up to 25 seconds in which entering a private area
 * does nothing visible at all (#80).
 *
 * @internal
 */
const VideoConnectingPanel: React.FC<{ placement?: StatusPlacement }> = ({ placement }) => (
  <VideoStatusPanel borderClass="border-game-accent/30" testId="video-connecting-panel" placement={placement}>
    <div className="flex items-center">
      <span className="text-game-accent text-lg mr-3 animate-pulse" aria-hidden="true">🎥</span>
      <p className="text-sm text-game-accent">{VIDEO_CONFIG.messages.connecting}</p>
    </div>
  </VideoStatusPanel>
);

/**
 * Reconnecting panel.
 * @internal
 */
const VideoReconnectingPanel: React.FC<{
  connectionAttempts: number;
  maxReconnectAttempts: number;
  placement?: StatusPlacement;
}> = ({ connectionAttempts, maxReconnectAttempts, placement }) => (
  <VideoStatusPanel borderClass="border-yellow-500/30" testId="video-reconnecting-panel" placement={placement}>
    <div className="flex items-center">
      <span className="text-yellow-500 text-lg mr-3 animate-pulse" aria-hidden="true">🔄</span>
      <div>
        <p className="text-sm text-yellow-400">Reconnecting to video...</p>
        <p className="text-xs text-game-light/60">
          Attempt {connectionAttempts}/{maxReconnectAttempts}
        </p>
      </div>
    </div>
  </VideoStatusPanel>
);

/** Props for {@link VideoOverlay} */
export interface VideoOverlayProps {
  /** The call's label in the full-screen top bar: the private area's name. */
  callLabel?: string;

  /**
   * Width the chat drawer reports (0 when closed). Full screen starts to its
   * right on md and up, so the drawer pushes the video aside.
   */
  chatDrawerWidth?: number;
}

/**
 * VideoOverlay component displays the video call interface.
 *
 * Audio and video have different lifetimes on purpose. The tiles follow
 * `useVideoOverlayVisibility`, which deliberately hides them when the user is
 * alone with the camera off. Audio follows the *room*: as long as a room
 * object exists we render {@link VideoRoomAudio}, so remote participants stay
 * audible in exactly the audio-only case the overlay hides. It keeps the same
 * place in the tree in every view, so entering or leaving full screen never
 * remounts it and never interrupts what anyone hears.
 *
 * The view (corner tiles or full screen, #123) comes from the
 * `VideoViewProvider`. It is read optionally: rendered without one, the
 * overlay is the corner tiles alone, with no "Full screen" pill.
 *
 * @component
 * @example
 * ```tsx
 * // Typically rendered at the app root level
 * <VideoOverlay />
 * ```
 */
const VideoOverlayComponent: React.FC<VideoOverlayProps> = ({ callLabel, chatDrawerWidth = 0 }) => {
  const {
    room,
    error,
    errorClassification,
    getRecoveryActions,
    isReconnecting,
    connectionAttempts,
    maxReconnectAttempts,
    retryConnection,
    currentPrivateArea
  } = useVideoCall();

  const {
    shouldShowOverlay,
    shouldShowError,
    shouldShowReconnecting,
    shouldShowConnecting
  } = useVideoOverlayVisibility();

  const view = useContext(VideoViewContext);
  const enterFullScreen = view?.enterFullScreen;
  const exitFullScreen = view?.exitFullScreen;
  // The provider closes full screen when the call ends; checking here too
  // means the render in between never shows an empty overlay.
  const showFullScreen =
    Boolean(view?.isFullScreen) && isCallLive({ room, isReconnecting, currentPrivateArea });

  // Leaving full screen puts focus back on the pill that entered it. The pill
  // takes focus when it mounts; if it does not come back (nothing to show in
  // the corner), the request is dropped rather than left to steal focus later.
  const returnFocusRef = useRef(false);
  const handleExit = useCallback(() => {
    returnFocusRef.current = true;
    exitFullScreen?.();
  }, [exitFullScreen]);
  useEffect(() => {
    if (!showFullScreen) returnFocusRef.current = false;
  }, [showFullScreen]);

  const placement = showFullScreen ? 'fullscreen' : 'hud';

  // Status panel: an error outranks the reconnecting notice, which outranks
  // the first-connection notice.
  let statusPanel: React.ReactNode = null;
  if (shouldShowError && error) {
    statusPanel = (
      <VideoErrorPanel
        message={errorClassification?.userMessage || error.message}
        recoveryActions={getRecoveryActions?.() ?? []}
        connectionAttempts={connectionAttempts}
        maxReconnectAttempts={maxReconnectAttempts}
        // The *context* knows whether a retry is in flight. The visibility
        // hook cannot: it makes the error and reconnecting flags mutually
        // exclusive, so gating this on `shouldShowReconnecting` meant the
        // "Retrying..." feedback could never appear (#80).
        isRetrying={Boolean(isReconnecting)}
        onRetry={retryConnection}
        placement={placement}
      />
    );
  } else if (shouldShowReconnecting) {
    statusPanel = (
      <VideoReconnectingPanel
        connectionAttempts={connectionAttempts}
        maxReconnectAttempts={maxReconnectAttempts}
        placement={placement}
      />
    );
  } else if (shouldShowConnecting) {
    statusPanel = <VideoConnectingPanel placement={placement} />;
  }

  const pill = enterFullScreen ? (
    <FullScreenPill onClick={enterFullScreen} returnFocusRef={returnFocusRef} />
  ) : undefined;

  // No room means nothing to play. Full screen stays up only through a
  // reconnect, with its panel; otherwise there is only the status panel.
  if (!room) {
    if (showFullScreen) {
      return (
        <FullScreenVideo
          label={callLabel}
          chatDrawerWidth={chatDrawerWidth}
          status={statusPanel}
          onExit={handleExit}
        />
      );
    }
    return <>{statusPanel}</>;
  }

  return (
    <>
      <RoomContext.Provider value={room}>
        <VideoRoomAudio room={room} />
        <CallView
          showFullScreen={showFullScreen}
          showCorner={shouldShowOverlay}
          actions={pill}
          label={callLabel}
          chatDrawerWidth={chatDrawerWidth}
          status={statusPanel}
          onExit={handleExit}
        />
      </RoomContext.Provider>
      {!showFullScreen && statusPanel}
    </>
  );
};

// Export memoized component for performance optimization
export const VideoOverlay = memo(VideoOverlayComponent);
VideoOverlay.displayName = 'VideoOverlay';
